import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "../../../i18n";
import Modal from "../../../shared/modal/Modal";
import css from "./StrongAuthentificationModal.module.scss";
import authentication_waiting from '../../../static/json-animations/authentication_waiting.json';
import authentication_success from '../../../static/json-animations/authentication_success.json';
import useInterval from "@/components/common/hooks/useInterval";
import UserActionsService from "@/services/domain/UserActionsService";
import { STRONG_AUTH_STATUS } from "@/models/StrongAuth";

const namespacesRequired = ["common", "authentication"];

const StrongAuthentificationModal = ({
  visible,
  onHide,
  strongAuthReference,
  action,
  onSuccess,
  t
}) => {
  const TIMEOUT_MS = 5 * 60 * 1000; // auto-close after 5 minutes
  const [LottieComponent, setLottieComponent] = useState(null);
  const [confirmed, setConfirmed] = useState(null);
  const [animationDimension, setAnimationDimension] = useState(400);
  const onHideRef = useRef(onHide);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    onHideRef.current = onHide;
  }, [onHide]);

  useEffect(() => {
    const loadLottie = async () => {
      const module = await import("react-lottie");
      setLottieComponent(() => module.default);
    };
    loadLottie();
  }, []);

  useEffect(() => {
    if (visible) {
      setConfirmed(null);
      setExpired(false);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    const timeout = setTimeout(() => {
      setExpired(true);
      onHideRef.current?.(STRONG_AUTH_STATUS.FAILED);
    }, TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [visible]);

  useEffect(() => {
    if (window.innerWidth < 875) {
      setAnimationDimension(Math.max(Math.trunc((window.innerWidth * 90) / 100), 400));
    }
  }, []);

  useInterval(() => {
    if (visible && !expired) {
      UserActionsService.checkStrongAuth(strongAuthReference)
        .then(result => {
          const resp = result?.strong_auth ? result.strong_auth : result;

          if (resp.status === STRONG_AUTH_STATUS.SUCCEEDED) {
            // Xpollens can report Status "Succeeded" (SCA challenge resolved) while
            // the underlying operation failed (e.g. invalid IBAN/BIC → Code 185).
            // Only the onHide path (no onSuccess / action) needs this intercept;
            // flows using onSuccess already inspect resp.payload themselves.
            const xpollensError = !onSuccess && !action && resp.payload?.Code
              ? resp.payload
              : null;

            if (xpollensError) {
              onHide?.(STRONG_AUTH_STATUS.FAILED, xpollensError);
            } else {
              setConfirmed(true);
              setTimeout(() => {
                if (onSuccess) {
                  onSuccess(resp);
                } else if (action) {
                  action(resp.additional_info?.cashout_reference);
                } else {
                  onHide?.(STRONG_AUTH_STATUS.SUCCEEDED);
                }
              }, 1000);
            }
          } else if (resp.status === STRONG_AUTH_STATUS.FAILED) {
            onHide?.(STRONG_AUTH_STATUS.FAILED);
          }
        })
        .catch(() => {
          onHide?.(STRONG_AUTH_STATUS.FAILED);
        });
    }
  }, 5000);

  const authWaitingOptions = {
    loop: true,
    autoplay: true,
    animationData: authentication_waiting,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice"
    }
  };

  const authSuccessOptions = {
    loop: false,
    autoplay: true,
    animationData: authentication_success,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice"
    }
  };

  return (
    <Modal
      visible={visible}
      title={t("authentication:strong-authentication.title")}
      closeOnOverlayClick={false}
      hideCloseButton={true}
      closeOnEsc={false}
    >
      <div className={css.sauth}>
        {!LottieComponent ? (
          <div>Loading animation...</div>
        ) : (
          <>
            {confirmed === null && (
              <LottieComponent
                isStopped={false}
                options={authWaitingOptions}
                height={animationDimension}
                width={animationDimension}
                speed={0.5}
              />
            )}
            {confirmed === true && (
              <LottieComponent
                eventListeners={[{
                  eventName: 'loopComplete',
                  callback: () => {
                    console.log("done here");
                  }
                }]}
                isStopped={false}
                options={authSuccessOptions}
                height={animationDimension}
                width={animationDimension}
                speed={0.5}
              />
            )}
            {confirmed === false && (
              <span>Error animation here</span>
            )}
          </>
        )}
        <span>{t("authentication:strong-authentication.must-authenticate")}</span>
      </div>
    </Modal>
  );
};

export default withTranslation(namespacesRequired)(StrongAuthentificationModal);
